import uniqueId from 'lodash/uniqueId';
import { ReactNode, useEffect, useState } from 'react';

import { useSelectedMaterial } from '~/api/easy-estimator-api/materials-api/hooks/useSelectedMaterial';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import {
  useLazyFetchInStoreCookingAppliancesQuery,
  useLazyFetchPredefinedCookingAppliancesQuery,
} from '~/api/local-api/cooking-appliances.api/cookingAppliancesService';
import { ProductType, SubsectionButtonOption } from '~/core/enums';
import { SectionTypes } from '~/core/enums/section-type-enum';
import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import {
  ApplianceModel,
  ApplianceSource,
  ApplianceType,
  Feature,
  ProductDetails,
} from '~/core/models';
import {
  addMockImage,
  buildQteAttributes,
  createTypeSafeContext,
  isSomething,
  updateListAndFilters,
} from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  resetBrowsedCookingAppliances,
  selectBrowsedCookingAppliances,
  selectSelectedBrowsingOption,
  updateBrowsedCookingAppliances,
  updateSuppliedCookingAppliances,
} from '~/redux/slices/cookingAppliance';
import {
  selectSelectedFilters,
  updateFilters,
} from '~/redux/slices/sortAndFilter';
import { selectZipcode } from '~/redux/slices/zipcode';

interface CookingApplianceContextValue {
  cookingAppliancesToProductDetails: () => ProductDetails[];
  loading: boolean;
}

export const [useCookingAppliancesContext, CookingAppliancesContext] =
  createTypeSafeContext<CookingApplianceContextValue>();

/**
 * @deprecated This context provider should instead be handled by RTK Query hooks
 * similarly to how `useSinks` or `useEdges` do
 */
export const CookingAppliancesContextProvider = ({
  children,
}: {
  children?: ReactNode;
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const selectedFilters = useAppSelector(selectSelectedFilters('cooking'));
  const browsedCookingAppliances = useAppSelector(
    selectBrowsedCookingAppliances
  );
  const selectedBrowsingOption = useAppSelector(selectSelectedBrowsingOption);
  const selectedMaterial = useSelectedMaterial();
  const selectedStore = useSelectedStore();
  const zipcode = useAppSelector(selectZipcode);

  const cookingAppliancesToProductDetails = (): ProductDetails[] =>
    browsedCookingAppliances.map((browsedCookingAppliance) => {
      const detail: ProductDetails = {
        productId: browsedCookingAppliance.productId,
        name: browsedCookingAppliance.name,
        consumerDesc: browsedCookingAppliance.consumerDesc,
        productType: ProductType.COOKING_APPLIANCE,
        price: browsedCookingAppliance.price,
        priceWithoutDiscount: browsedCookingAppliance.priceWithoutDiscount,
        expirationPromotionDate:
          browsedCookingAppliance.expirationPromotionDate,
        abbrevDesc: browsedCookingAppliance.abbrevDesc,
        unitOfMeasure: browsedCookingAppliance.unitOfMeasure,
        ...buildQteAttributes(browsedCookingAppliance),
      };

      return detail;
    });

  ///// Legacy useCookingAppliance /////
  const [loading, setLoading] = useState(false);
  const { getInStoreApplianceUrl } = useRetailerSettings();
  const [triggerPredefinedCookingAppliancesQry] =
    useLazyFetchPredefinedCookingAppliancesQuery();
  const [triggerInStoreCookingAppliancesQry] =
    useLazyFetchInStoreCookingAppliancesQuery();

  useEffect(() => {
    const controller = new AbortController();
    const updateAppliancesBy: Record<SubsectionButtonOption, () => void> = {
      [SubsectionButtonOption.SUPPLY]: async () => {
        try {
          setLoading(true);

          if (!selectedMaterial?.qteGrpID) {
            return;
          }

          const { data } = await triggerPredefinedCookingAppliancesQry({
            params: {
              prodQteGrpID: selectedMaterial?.qteGrpID,
              storeId: selectedStore?.companyId,
              zipCode: zipcode,
            },
          });

          setLoading(false);
          if (data) {
            dispatch(
              updateSuppliedCookingAppliances(data.map(makeApplianceModel))
            );
          }
        } catch (err) {
          console.error('ERROR: ', err);
          setLoading(false);
        }
      },
      [SubsectionButtonOption.BROWSE]: async () => {
        try {
          setLoading(true);
          const url = getInStoreApplianceUrl(SectionTypes.cooking_appliance);
          const inStoreAppliances =
            await triggerInStoreCookingAppliancesQry(url);
          const data = inStoreAppliances.data || emptyArray;

          setLoading(false);
          const appliances = data.map((cookingAppliance) => ({
            ...addMockImage(cookingAppliance),
            uuid: uniqueId(),
            productSource: ApplianceSource.IN_STORE,
            features: [
              ...cookingAppliance.features,
              // TODO: remove once "Type" is added on the backend
              makeFeatureCookingApplianceType(cookingAppliance.type),
              makeFeatureProductSource(cookingAppliance.productSource),
            ],
          }));
          const { ids, filters } = updateListAndFilters(
            appliances,
            selectedFilters
          );
          const cookingAppliances = ids
            .map((id) => appliances.find((ca) => ca.productId === id))
            .filter(isSomething);

          if (cookingAppliances.length === 0) {
            return;
          }

          dispatch(
            updateFilters({ component: 'cooking', filters, selectedFilters })
          );
          dispatch(updateBrowsedCookingAppliances(cookingAppliances));
        } catch (err) {
          console.error('ERROR: ', err);
          setLoading(false);
        }
      },
      [SubsectionButtonOption.NONE]: () =>
        dispatch(resetBrowsedCookingAppliances()),
    };

    updateAppliancesBy[selectedBrowsingOption]();

    return () => {
      controller.abort();
    };
  }, [
    dispatch,
    getInStoreApplianceUrl,
    selectedBrowsingOption,
    selectedFilters,
    selectedMaterial?.qteGrpID,
    selectedStore?.companyId,
    triggerInStoreCookingAppliancesQry,
    triggerPredefinedCookingAppliancesQry,
    zipcode,
  ]);

  return (
    <CookingAppliancesContext.Provider
      value={{
        cookingAppliancesToProductDetails,
        loading,
      }}
    >
      {children}
    </CookingAppliancesContext.Provider>
  );
};

const emptyArray = [] satisfies ApplianceModel[];

const makeApplianceModel = (appliance: ApplianceModel): ApplianceModel => ({
  ...appliance,
  id: appliance.productId,
  uuid: '',
  name: appliance.consumerDesc,
  abbrevDesc: appliance.consumerDesc,
  beautyShotImageUrl: appliance.imageUrl,
  applianceType:
    appliance.type === 'Cooktop' ? ApplianceType.COOKTOP : ApplianceType.RANGE,
  brand: '',
  features: [],
  productSource: ApplianceSource.CUSTOMER,
  promos: [],
  rank: 0,
  // $$
  discountPercentage: null,
  price: 0,
  priceWithoutDiscount: 0,
  // 3d?
  length: 0,
  width: 0,
  height: 0,
  depth: null,
  // qte
  qteCode: null,
  qteCodeId: null,
  qteSubCode: null,
  qteSubCodeId: null,
  qteTypeCode: null,
  qteTypeId: null,
});

const makeFeatureCookingApplianceType = (
  cookingApplianceType: string
): Feature => ({
  name: 'cookingApplianceType',
  description: 'Type',
  position: 10,
  value: cookingApplianceType,
});

const makeFeatureProductSource = (
  cookingApplianceProductSource: ApplianceSource
): Feature => ({
  name: 'productSource',
  description: 'Product Source',
  position: 11,
  value:
    cookingApplianceProductSource === ApplianceSource.PROGRAM
      ? ApplianceSource.PROGRAM
      : ApplianceSource.IN_STORE,
});
